import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { COMMUNITY_API_PREFIX } from 'shared/api/constants/api-constants'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import { PostStatusEnum } from 'modules/community/enums/post-status-enum'
import type { PostInterface } from 'modules/community/types/post-interface'
import { communityPostKeys } from './key-factory'

interface PostPages {
  pages: { items: PostInterface[] }[]
  pageParams: unknown[]
}

interface UsePostModerationOptions {
  communityPath: string
  target: 'approve' | 'decline'
  onSuccess?: () => void
  onError?: (error: Error) => void
}

const STATUS_BY_TARGET = {
  approve: PostStatusEnum.Approved,
  decline: PostStatusEnum.Declined,
} as const satisfies Record<UsePostModerationOptions['target'], PostStatusEnum>

const PATH_BY_TARGET = {
  approve: 'publish-multiple',
  decline: 'decline',
} as const satisfies Record<UsePostModerationOptions['target'], string>

const setPostStatus = (oldData: unknown, postId: number, status: PostStatusEnum): unknown => {
  if (oldData === null || typeof oldData !== 'object') return oldData
  if ('pages' in oldData) {
    const data = oldData as PostPages
    return {
      ...data,
      pages: data.pages.map(page => ({
        ...page,
        items: page.items.map(post => (post.id === postId ? { ...post, status } : post)),
      })),
    }
  }
  if (Array.isArray(oldData)) {
    return oldData.map(post =>
      (post as PostInterface).id === postId ? { ...(post as PostInterface), status } : post,
    )
  }
  const post = oldData as PostInterface
  return post.id === postId ? { ...post, status } : oldData
}

export const usePostModeration = ({
  communityPath,
  target,
  onSuccess,
  onError,
}: UsePostModerationOptions): UseMutationResult<void, Error, number> => {
  const queryClient = useQueryClient()

  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.post,
    void,
    { ids: number[] }
  >({
    method: RequestMethodsEnum.post,
    badRequestHandler: target === 'approve' ? () => {} : undefined,
  })

  return useMutation({
    mutationFn: (postId: number) =>
      fetcher(
        `${COMMUNITY_API_PREFIX}/${communityPath}/moderation/posts/${PATH_BY_TARGET[target]}`,
        {
          ids: [postId],
        },
      ),

    onMutate: async (postId: number) => {
      await queryClient.cancelQueries({ queryKey: communityPostKeys.all })
      const snapshot = queryClient.getQueriesData({ queryKey: communityPostKeys.all })

      const status = STATUS_BY_TARGET[target]
      queryClient.setQueriesData({ queryKey: communityPostKeys.all }, old => {
        if (old === undefined) return old
        return setPostStatus(old, postId, status)
      })

      return { snapshot }
    },

    onError: (error, _postId, context) => {
      if (context?.snapshot) {
        for (const [key, data] of context.snapshot) {
          queryClient.setQueryData(key, data)
        }
      }
      onError?.(error)
    },

    onSuccess: () => onSuccess?.(),

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: communityPostKeys.all })
    },
  })
}
